import { FC } from 'react';
import {
	BrandName,
	CardContainer,
	CardContent,
	CardContentContainer,
	CardTop,
	ProductName,
} from './productCard.styled';
import { Separator } from './separator/separator.styled';
import { Button } from '@components/core/button/Button';
import RatingList from './ratingList/RatingList';
import FeatureList from './featureList/FeatureList';
import TrustRating from './trustRating/TrustRating';
import parse from 'html-react-parser';
import ProsCheckmark from '@assets/icons/ProsCheckmark';
import ConsCross from '@assets/icons/ConsCross';
import { useACF } from '@context/ACFContext';
import { OtherProducts } from 'types/globalTypes';
import { useVisibleProducts } from '@hooks/useVisibleProducts';
import Toggle from '@components/core/toggle/Toggle';
import ProductImage from './productImage/ProductImage';

const CompetitorProductCard: FC = () => {
	const { postTemplate } = useACF();
	const products = postTemplate?.review_page_v4_other_products_section_settings;

	if (!products || !products.length) return null;

	const { visibleItems: visibleProducts, toggleShowAll } = useVisibleProducts(products, 2);

	return (
		<>
			{visibleProducts.map((product: OtherProducts, index: number) => (
				<CardContainer
					key={index}
					$borderColor={product.card_border_color}
					$borderWidth={product.card_border_width}
					$withShadow={false}
				>
					<CardTop>
						<ProductName>{product.product_name}</ProductName>
						<Separator $height="24px" $color="#333" />
						<BrandName>{parse(product.tr_best_choice_label)}</BrandName>
					</CardTop>

					<CardContent>
						<CardContentContainer>
							<ProductImage productImage={product.product_image} />

							<RatingList
								$barBackground={product.rating_bar_background_color}
								$barColor={product.rating_bar_color}
								productImage={product.product_image}
								ratingRepeater={product.rating_repeater}
								prosRepeater={product.product_pros}
								consRepeater={product.product_cons}
								reviewScoreLabel={product.tr_review_score_label}
							/>
						</CardContentContainer>

						<CardContentContainer>
							<FeatureList
								data={
									product.product_pros &&
									product.product_pros?.map((item) => ({
										label: item.tr_product_pro,
									}))
								}
								title={product.tr_pros_label}
								icon={<ProsCheckmark />}
								color="#00a871"
							/>
						</CardContentContainer>

						<CardContentContainer>
							<FeatureList
								data={
									product.product_cons &&
									product.product_cons?.map((item) => ({
										label: item.tr_product_con,
									}))
								}
								title={product.tr_cons_label}
								icon={<ConsCross />}
								color="#D2152D"
							/>
						</CardContentContainer>

						<CardContentContainer>
							<TrustRating
								reviewLabel={product.trustpilot_component.tr_trust_pilot_review_score_label}
								reviewScore={product.trustpilot_component.trust_pilot_rating_score}
								reviewEvaluation={product.trustpilot_component.tr_trust_pilot_excellent_label}
								reviewCount={product.trustpilot_component.tr_trust_pilot_customer_reviews_label}
								reviewImage={product.trustpilot_component.trust_pilot_rating_image}
							/>
							<Button
								href={product.product_card_link?.url}
								target={product.product_card_link?.target}
								primary={false}
							>
								{product.button_component.tr_button_text}
							</Button>
						</CardContentContainer>
					</CardContent>
				</CardContainer>
			))}

			{products.length > 2 && (
				<Toggle
					onClick={toggleShowAll}
					openedLabel={postTemplate?.toggle_button_component.tr_see_less_label}
					closedLabel={postTemplate?.toggle_button_component.tr_see_full_list_label}
				></Toggle>
			)}
		</>
	);
};

export default CompetitorProductCard;
